import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpRequest } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { catchError, last, map, of, Subscription, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DisputeFileUpload } from '../model/af.model';
import { EventService } from '../service/event.service';
import { AttachmentUploadedEvent, UploadEvent } from '../service/events';

@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    animations: [
        trigger('fadeInOut', [
            state('in', style({ opacity: 100 })),
            transition('* => void', [
                animate(300, style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})

export class FileUploadComponent implements OnInit, OnDestroy {
      /** Link text */
      @Input() text = 'Choose File';
      /** Name used in form which will be sent in HTTP request. */
      @Input() param = 'file';
      /** Target URL for file uploading. */
      @Input() target = environment.api_uri + "uploadApplicantAttachments";

      @Input() updateTarget = environment.api_uri + "updateAttachment";

      @Input() disputeTarget = environment.api_uri + "uploadApplicantDisputeAttachments";
      /** File extension that accepted, same as 'accept' of <input type="file" />. 
          By the default, it's set to 'image/*'. */
      @Input() accept = 'image/*';
      /** Allow you to add handler after its completion. Bubble up response text from remote. */
      @Output() complete = new EventEmitter<string>();

      @Input('descOfFileName') descOfFileName: AbstractControl;

      @Input('verifyId') verifyId: AbstractControl;

      @Input('ousAttachments') ousAttachments: boolean;

      @Input('ousAttachmentId') ousAttachmentId: string;

      @Input('ousAttachmentDesc') ousAttachmentDesc: string;

      @Input('updateFlag') updateFlag: string;

      @Input('disputeFileUpload') disputeFileUpload: DisputeFileUpload;

      @Input('fileFormat') fileFormat: string;

      sub: Subscription;
      selected: boolean = true;

      @ViewChild("fileUpload") fileUploadElement: ElementRef;

      files: Array<FileUploadModel> = [];

      constructor(private _http: HttpClient, private eventBus: EventService,) { }

      ngOnInit() {
            this.sub = this.eventBus.on(
                  UploadEvent,
                  (): void => {
                        this.uploadFiles();
                  }
            );
      }
      ngOnDestroy(): void {
            this.sub.unsubscribe();
      }

      onClick() {
            const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
            fileUpload.onchange = () => {
                  this.selected = true;
                  for (const file of Array.from(fileUpload.files)) {
                        this.files.splice(0, this.files.length);
                        this.files.push({
                              data: file, state: 'in',
                              inProgress: false, progress: 0, canRetry: false, canCancel: true, error: null
                        });
                  }
            };

            fileUpload.click();
      }

      cancelFile(file: FileUploadModel) {
            if (file.sub) {
                  file.sub.unsubscribe();
            }

            this.removeFileFromArray(file);
      }

      retryFile(file: FileUploadModel) {
            this.uploadFile(file);
            file.canRetry = false;
            file.error = null;
      }

      private uploadFile(file: FileUploadModel) {

            const fd = new FormData();
            fd.append(this.param, file.data);
            fd.append("verifyId", this.verifyId.value);
            fd.append("ousAttachmentId", this.ousAttachmentId ? this.ousAttachmentId : "");
            fd.append("descriptionOfFile", this.descOfFileName ? this.descOfFileName.value : "");
            fd.append("asiComponentId", this.disputeFileUpload ? this.disputeFileUpload.asiComponentId : "");
            fd.append("asiCaseId", this.disputeFileUpload ? this.disputeFileUpload.asiCaseId : "");
            fd.append("applicantFormId", this.disputeFileUpload ? this.disputeFileUpload.applicantFormId : "");
            fd.append("ousAttachmentDesc", this.ousAttachmentDesc ? this.ousAttachmentDesc : "");
            let targetUrl: string = this.target;
            if (this.disputeFileUpload) {
                  targetUrl = this.disputeTarget;
            }
            if (this.updateFlag? this.updateFlag == 'U': false) {
                  targetUrl = this.updateTarget;
            }
            const req = new HttpRequest('POST', targetUrl, fd, {
                  reportProgress: true
            });
            file.inProgress = true;
            this.eventBus.emit(new AttachmentUploadedEvent({ file: null, inProgress: true }));
            file.sub = this._http.request(req).pipe(
                  map(event => {
                        switch (event.type) {
                              case HttpEventType.UploadProgress:
                                    file.progress = Math.round(event.loaded * 100 / event.total);
                                    break;
                              case HttpEventType.Response:
                                    return event;
                        }
                  }),
                  tap(() => { }),
                  last(),
                  catchError((error: HttpErrorResponse) => {
                        file.inProgress = false;
                        file.canRetry = true;
                        return of(`${file.data.name} : ${error.error}`);
                  })
            ).subscribe(
                  (event: any) => {
                        if (typeof (event) === 'object') {
                              this.removeFileFromArray(file);
                        } else {
                              file.error = event;
                        }

                        this.eventBus.emit(new AttachmentUploadedEvent({ file: event.body, inProgress: false }));
                  }
            );
      }

      private uploadFiles() {
            const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
            fileUpload.value = '';
            this.selected = (this.files.length > 0);

            this.files.forEach(file => {
                  let filename: string = file.data.name;
                  let filetype: string = filename.substring(filename.lastIndexOf(".") + 1).toLowerCase();
                  if (!this.fileFormat.toLowerCase().includes(filetype)) {
                        file.error = "Invalid File Formats";
                        return "Invalid File Formats";
                  }
                  this.uploadFile(file);
            });
      }

      private removeFileFromArray(file: FileUploadModel) {
            const index = this.files.indexOf(file);
            if (index > -1) {
                  this.files.splice(index, 1);
            }
      }
}

export class FileUploadModel {
      data: File;
      state: string;
      inProgress: boolean;
      progress: number;
      canRetry: boolean;
      canCancel: boolean;
      sub?: Subscription;
      error: string;
}
